import { AlertOctagon, AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { Badge } from '../ui/Badge';
import { formatRelativeTime } from '../../utils/format';
import type { SecurityAlert, SecurityAlertSeverity } from '../../types';

const severityConfig: Record<SecurityAlertSeverity, { icon: typeof AlertOctagon; tone: 'danger' | 'warning' | 'neutral' }> = {
  critical: { icon: AlertOctagon, tone: 'danger' },
  warning: { icon: AlertTriangle, tone: 'warning' },
  info: { icon: Info, tone: 'neutral' },
};

function AlertCard({ alert }: { alert: SecurityAlert }) {
  const { icon: Icon, tone } = severityConfig[alert.severity];
  return (
    <div className="rounded-lg border border-slate-100 p-3 dark:border-slate-800">
      <div className="flex items-start gap-2.5">
        <Icon size={16} className={tone === 'danger' ? 'mt-0.5 shrink-0 text-rose-500' : tone === 'warning' ? 'mt-0.5 shrink-0 text-amber-500' : 'mt-0.5 shrink-0 text-slate-400'} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{alert.title}</p>
            <Badge tone={tone}>{alert.site}</Badge>
            {alert.compromised && <Badge tone="danger">Compromise suspected</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{alert.summary}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
            {alert.attackCount !== undefined && <span>{alert.attackCount} attack(s)</span>}
            {alert.blockedCount !== undefined && <span>{alert.blockedCount} blocked</span>}
            {alert.sourceIp && (
              <span>
                From {alert.sourceIp}
                {alert.sourceCountry ? ` (${alert.sourceCountry})` : ''}
              </span>
            )}
            <span>{formatRelativeTime(alert.receivedAt)}</span>
          </div>
          {alert.recommendation && (
            <p className="mt-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              {alert.recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function SecurityAlertsList({ alerts }: { alerts: SecurityAlert[] }) {
  if (alerts.length === 0) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="No website security alerts"
        description="Alerts appear here once Wordfence (or another source) is wired up to POST to the security alerts webhook."
      />
    );
  }

  return (
    <div className="space-y-3">
      {alerts.map((alert) => (
        <AlertCard key={alert.id} alert={alert} />
      ))}
    </div>
  );
}
